/**
 * Descodificador de códigos de barras.
 *
 * - Chrome no Android (os telemóveis da empresa): a `BarcodeDetector` nativa.
 * - Safari, Firefox, computador: o polyfill `barcode-detector` (zxing em WASM),
 *   carregado **só quando é preciso** (import dinâmico). O `.wasm` vai no
 *   bundle da app (servido como `application/wasm` pelo worker das apps) — por
 *   omissão o polyfill ia buscá-lo a um CDN.
 */

/**
 * Os formatos que se procuram em cada imagem. Além dos de sempre, os das
 * caixas (0.37.0): ITF (o ITF-14 dos cartões), UPC-E, Code 93, Codabar e
 * PDF417 — sem eles, um código destes nem chegava a ser lido. Os nomes são os
 * da `BarcodeDetector` (a nativa filtra pelos que suporta; o polyfill
 * `barcode-detector` 3.x aceita-os todos).
 */
export const FORMATS = [
  "code_128",
  "code_39",
  "code_93",
  "codabar",
  "ean_13",
  "ean_8",
  "upc_a",
  "upc_e",
  "itf",
  "qr_code",
  "data_matrix",
  "pdf417",
] as const

export interface DetectedCode {
  rawValue: string
  format: string
}

export interface Detector {
  detect(source: ImageBitmapSource): Promise<DetectedCode[]>
  native: boolean
}

interface NativeDetectorCtor {
  new (opts: { formats: string[] }): { detect(source: ImageBitmapSource): Promise<DetectedCode[]> }
  getSupportedFormats(): Promise<string[]>
}

let cached: Promise<Detector> | null = null

export function getDetector(): Promise<Detector> {
  cached ??= create().catch((error) => {
    cached = null
    throw error
  })
  return cached
}

async function create(): Promise<Detector> {
  const Native = (globalThis as { BarcodeDetector?: NativeDetectorCtor }).BarcodeDetector
  if (Native) {
    try {
      const supported = await Native.getSupportedFormats()
      const formats = FORMATS.filter((f) => supported.includes(f))
      // Sem Code 128 não lê as etiquetas de série: usa o polyfill.
      if (formats.includes("code_128")) {
        const d = new Native({ formats })
        return { native: true, detect: (s) => d.detect(s) }
      }
    } catch {
      // cai no polyfill
    }
  }
  const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
    import("barcode-detector/ponyfill"),
    import("zxing-wasm/reader/zxing_reader.wasm?url"),
  ])
  prepareZXingModule({
    overrides: { locateFile: (path: string, prefix: string) => (path.endsWith(".wasm") ? wasmUrl : prefix + path) },
  })
  const d = new BarcodeDetector({ formats: [...FORMATS] })
  return { native: false, detect: (s) => d.detect(s as never) as Promise<DetectedCode[]> }
}

/** Descodifica uma foto (plano B: sem câmara ao vivo). */
export async function detectInFile(file: Blob): Promise<string[]> {
  const detector = await getDetector()
  const bitmap = await createImageBitmap(file)
  try {
    const found = await detector.detect(bitmap)
    return [...new Set(found.map((c) => c.rawValue).filter(Boolean))]
  } finally {
    bitmap.close()
  }
}
